import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Modal } from "antd";
import btnStyles from "../../../styles/Custom/Button.module.css";
import {
  privacyPolicyContent,
  termsOfUseContent,
} from "../../../constants/legalContent";
import styles from "./TermsAndConditionsModal.module.css";

const DOC_STEPS = [
  { key: "terms", title: "Terms of Use" },
  { key: "privacy", title: "Privacy Policy" },
];

const DOCUMENTS = [
  {
    key: "terms",
    title: "Terms of Use",
    updated: "April 2026",
    sections: termsOfUseContent,
    skipTitles: ["TERMS OF USE"],
  },
  {
    key: "privacy",
    title: "Privacy Policy",
    updated: "April 2026",
    sections: privacyPolicyContent,
    skipTitles: [],
  },
];

const SCROLL_THRESHOLD = 24;

const formatSectionTitle = (title) => {
  if (!title) return "";
  const isAllCaps = title === title.toUpperCase() && /[A-Z]/.test(title);
  if (!isAllCaps) return title;

  return title
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getNavSections = (sections, skipTitles = []) =>
  sections
    .filter(
      (section) =>
        (section.title || section.paragraphs?.length) &&
        !skipTitles.includes(section.title?.toUpperCase())
    )
    .map((section, index) => ({
      ...section,
      navIndex: index,
      label: formatSectionTitle(section.title) || `Section ${index + 1}`,
    }));

const createEmptyReadState = () =>
  DOCUMENTS.reduce((acc, doc) => {
    acc[doc.key] = new Set();
    return acc;
  }, {});

const TermsAndConditionsModal = ({ open, onAccept, acceptLoading = false }) => {
  const [activeDocKey, setActiveDocKey] = useState("terms");
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [accepted, setAccepted] = useState(false);
  const [readSections, setReadSections] = useState(createEmptyReadState);
  const contentBodyRef = useRef(null);

  const activeDocument = useMemo(
    () => DOCUMENTS.find((doc) => doc.key === activeDocKey) || DOCUMENTS[0],
    [activeDocKey]
  );

  const navSections = useMemo(
    () => getNavSections(activeDocument.sections, activeDocument.skipTitles),
    [activeDocument]
  );

  const documentSectionsMap = useMemo(
    () =>
      DOCUMENTS.reduce((acc, doc) => {
        acc[doc.key] = getNavSections(doc.sections, doc.skipTitles);
        return acc;
      }, {}),
    []
  );

  const activeSection = navSections[activeSectionIndex] || navSections[0];

  const canContinue = accepted;

  const markAllSectionsAsRead = useCallback(() => {
    setReadSections(
      DOCUMENTS.reduce((acc, doc) => {
        const sections = getNavSections(doc.sections, doc.skipTitles);
        acc[doc.key] = new Set(sections.map((_, index) => index));
        return acc;
      }, {})
    );
  }, []);

  const markSectionAsRead = useCallback((docKey, sectionIndex) => {
    setReadSections((prev) => {
      if (prev[docKey]?.has(sectionIndex)) {
        return prev;
      }

      const nextDocRead = new Set(prev[docKey]);
      nextDocRead.add(sectionIndex);

      return {
        ...prev,
        [docKey]: nextDocRead,
      };
    });
  }, []);

  const isSectionFullyVisible = useCallback((element) => {
    if (!element) return false;

    const { scrollTop, scrollHeight, clientHeight } = element;
    return scrollHeight <= clientHeight + 1 ||
      scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD;
  }, []);

  const handleContentScroll = useCallback(() => {
    const element = contentBodyRef.current;
    if (!element || !isSectionFullyVisible(element)) {
      return;
    }

    markSectionAsRead(activeDocKey, activeSectionIndex);
  }, [
    activeDocKey,
    activeSectionIndex,
    isSectionFullyVisible,
    markSectionAsRead,
  ]);

  useEffect(() => {
    if (open) {
      setActiveDocKey("terms");
      setActiveSectionIndex(0);
      setAccepted(false);
      setReadSections(createEmptyReadState());
    }
  }, [open]);

  useEffect(() => {
    if (contentBodyRef.current) {
      contentBodyRef.current.scrollTop = 0;
    }
  }, [activeDocKey, activeSectionIndex]);

  useEffect(() => {
    const element = contentBodyRef.current;
    if (!element) return undefined;

    const markIfFullyVisible = () => {
      if (isSectionFullyVisible(element)) {
        markSectionAsRead(activeDocKey, activeSectionIndex);
      }
    };

    const frameId = window.requestAnimationFrame(markIfFullyVisible);

    return () => window.cancelAnimationFrame(frameId);
  }, [
    activeDocKey,
    activeSectionIndex,
    activeSection,
    isSectionFullyVisible,
    markSectionAsRead,
  ]);

  const handleDocumentChange = (key) => {
    setActiveDocKey(key);
    setActiveSectionIndex(0);
  };

  const handleAgreementChange = (e) => {
    const checked = e.target.checked;
    setAccepted(checked);

    if (checked) {
      markAllSectionsAsRead();
      const privacySections = documentSectionsMap.privacy || [];
      const lastPrivacyIndex = Math.max(0, privacySections.length - 1);
      setActiveDocKey("privacy");
      setActiveSectionIndex(lastPrivacyIndex);
    }
  };

  return (
    <Modal
      open={open}
      centered
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      title={null}
      width={920}
      destroyOnClose
      wrapClassName={styles.modalWrap}
      styles={{
        content: { padding: 0, borderRadius: 12, overflow: "hidden" },
        body: { padding: 0 },
      }}
    >
      <div className={styles.modal}>
        <div className={styles.bodyLayout}>
          <aside className={styles.sidebar}>
            <nav className={styles.sectionNav} aria-label="Document sections">
              {navSections.map((section, index) => (
                <button
                  key={`${section.title}-${index}`}
                  type="button"
                  className={`${styles.navItem} ${
                    activeSectionIndex === index ? styles.navItemActive : ""
                  }`}
                  onClick={() => setActiveSectionIndex(index)}
                >
                  <span className={styles.navNumber}>{index + 1}</span>
                  <span className={styles.navLabel}>{section.label}</span>
                </button>
              ))}
            </nav>
          </aside>

          <div className={styles.rightPanel}>
            <div className={styles.topBar}>
              <div className={styles.docStepsWrapper}>
                <div className={styles.docStepBar} role="tablist">
                  {DOC_STEPS.map((doc, index) => {
                    const isActive = activeDocKey === doc.key;

                    return (
                      <React.Fragment key={doc.key}>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={isActive}
                          className={styles.docStepButton}
                          onClick={() => handleDocumentChange(doc.key)}
                        >
                          <span
                            className={`${styles.docStepIcon} ${
                              isActive ? styles.docStepIconActive : ""
                            }`}
                          >
                            {index + 1}
                          </span>
                          <span
                            className={`${styles.docStepLabel} ${
                              isActive ? styles.docStepLabelActive : ""
                            }`}
                          >
                            {doc.title}
                          </span>
                        </button>
                        {index < DOC_STEPS.length - 1 && (
                          <span className={styles.docStepLine} aria-hidden="true" />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
              <div className={styles.sectionDivider} />
            </div>

            <div className={styles.contentArea}>
              <div
                ref={contentBodyRef}
                className={styles.contentBody}
                onScroll={handleContentScroll}
              >
                <h3 className={styles.activeSectionTitle}>
                  {activeSection?.label}
                </h3>

                <div className={styles.sectionContent}>
                  {activeSection?.paragraphs?.map((paragraph, index) => (
                    <p key={index} className={styles.paragraph}>
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <footer className={styles.footer}>
          <div className={styles.footerLeft}>
            <label className={styles.agreementRow}>
              <Checkbox
                checked={accepted}
                onChange={handleAgreementChange}
              />
              <span>
                I have read and agree to the Terms of Use and Privacy Policy
              </span>
            </label>
          </div>

          <Button
            type="primary"
            className={`${btnStyles.mdBtn} ${styles.acceptButton}`}
            disabled={!canContinue}
            loading={acceptLoading}
            onClick={onAccept}
          >
            I Agree
          </Button>
        </footer>
      </div>
    </Modal>
  );
};

export default TermsAndConditionsModal;
